import { isEmpty, eq, size, partition, find } from 'lodash/fp';
import { Stack, Text, Box } from '@chakra-ui/react';
import { Empty } from '@halo/ui-chat-react';
import type { User } from '@halo/chat-sdk-react';
import { useAppStore } from '@halo/chat-sdk-react';
import type { SearchResults } from './helpers';
import { ResultGroupItem } from './ResultsByGroup';
import { ResultUser } from './ResultContacts';

interface ResultGlobalProps {
  searchValue: string;
  searchResults: SearchResults;
}

const ResultGlobal = ({ searchResults }: ResultGlobalProps) => {
  const { t } = useTranslation();
  const { globalData } = searchResults;

  const [contacts, setContacts] = useState<User[]>();
  const { client } = useAppStore();

  const getContacts = useCallback(async () => {
    const { users } = (await client?.Api.contacts.contactsSearchByKeyInLocal('')) ?? {};
    setContacts(users);
  }, []);

  useEffect(() => {
    getContacts();
  }, []);

  const excludeFromContacts = useMemo(() => {
    const [_, user] = partition(
      (item) =>
        find((contact) => item.target_type == 1 && contact.user_id === item.target_id, contacts),
      globalData
    );

    return user;
  }, [contacts, globalData]);

  if (isEmpty(excludeFromContacts)) {
    return <Empty className="fadeIn" />;
  }

  return (
    <Box>
      <Text fontSize="xs" color="fg.muted" fontWeight="medium" p="2.5">
        {t('filter.globalTitle')}
      </Text>
      <Stack gap="2" className="fadeIn List" bg="bg" p="1" borderRadius="lg">
        {excludeFromContacts?.map((item, index) => {
          if (Number(item.target_type) === 1) {
            return (
              <ResultUser
                key={item.target_id}
                item={{
                  ...item,
                  name: item?.target_name,
                  username: item?.target_username,
                  avatar: item.target_avatar,
                  avatar_color: item.target_avatar_color,
                  user_id: item.target_id,
                }}
                hiddenBorder={eq(size(globalData), index + 1)}
              />
            );
          } else {
            return (
              <ResultGroupItem
                key={item.target_id}
                item={{
                  ...item,
                  group_name: item.target_name,
                  gid: item.target_id,
                  avatar: item.target_avatar,
                  avatar_color: item.target_avatar_color,
                  member_num: item.group_numbers,
                }}
                hiddenBorder={eq(size(globalData), index + 1)}
              />
            );
          }
        }, globalData)}
      </Stack>
    </Box>
  );
};

const MemoResultGlobal = memo(ResultGlobal);

export { MemoResultGlobal as ResultGlobal };
